import type { HttpRequestConfig } from '../services/HttpClient';
import { isBrowser } from './browserHelper';
import { getAppViewAccessToken } from './cookieHelper';
import { isTestMode } from './urlHelper';
import services from '../services';
import type { ConfirmAppointmentParams } from '../services/appointment/model';
import type { AppView } from '../types/app';
import { isAppViewGuestUser } from './appViewHelper';

const applyBearerToken = (token: string): HttpRequestConfig => ({
  headers: { Authorization: `Bearer ${token}` },
});

const isLocalEnv = TARGET === 'local';

const authTokenHandler = () => {
  const buildAuthConfig = async (view: AppView): Promise<HttpRequestConfig> => {
    if (!isBrowser()) return {};

    if (view === 'app') {
      const appViewAccessToken = getAppViewAccessToken();
      if (!appViewAccessToken) {
        if (isTestMode() || isAppViewGuestUser()) return {};
        throw new Error('Token not found');
      }
      return applyBearerToken(appViewAccessToken);
    }

    const isLoggedIn = window?.ikea?.authentication?.isLoggedIn;
    if (!isLoggedIn) return {};
    const payload = await isLoggedIn();
    if (!payload.isLoggedIn) return {};
    return applyBearerToken(payload.authToken);
  };

  const getUserInformation = async (view: AppView) => {
    const authConfig = isLocalEnv
      ? applyBearerToken('test-token')
      : await buildAuthConfig(view);
    return services.user.getUserInformation(authConfig);
  };

  const confirmAppointment = async (
    params: ConfirmAppointmentParams,
    view: AppView,
  ) => {
    const authConfig = isLocalEnv ? {} : await buildAuthConfig(view);
    return services.appointment.confirmAppointment(params, authConfig);
  };

  return { getUserInformation, confirmAppointment };
};

export const authTokenHandlerInstance = authTokenHandler();
